import {
  ChevronDownIcon,
  ChevronRightIcon,
} from '@langchain/untitled-ui-icons';
import { Checkbox } from '@mui/joy';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@radix-ui/react-collapsible';

import { useCallback, useMemo, useState } from 'react';

import { TextOverflowTooltip } from '@/components/TextOverflowTooltip';
import { Button, IconButton, TagInput, Text } from '@/design-system';
import { cn } from '@/utils/tailwind';

import {
  getConvertedNegativePath,
  interpolateNegativeIndices,
  useFilteredPaths,
  usePathTreeFromValues,
} from './utils/ConversationPathConfiguration.utils';

interface ConversationPathConfiguratorProps {
  availableInputPaths: string[];
  availableOutputPaths: string[];
  inputValues: unknown;
  outputValues: unknown;
  selectedInputPaths: string[];
  selectedOutputPaths: string[];
  onChangeInputPaths: (paths: string[]) => void;
  onChangeOutputPaths: (paths: string[]) => void;
}

export type PathNode = {
  name: string;
  path: string;
  value?: unknown;
  children: PathNode[];
};

function arraysEqual(next: string[], prev: string[]) {
  if (next.length !== prev.length) return false;
  return next.every((value, index) => value === prev[index]);
}

function PathTreeNode({
  node,
  depth,
  selectedPaths,
  onTogglePath,
  expandedState,
  onToggleExpand,
  arrayExpandedState,
  onToggleArrayExpand,
}: {
  node: PathNode;
  depth: number;
  selectedPaths: string[];
  onTogglePath: (path: string, selected: boolean) => void;
  expandedState: Record<string, boolean>;
  onToggleExpand: (path: string) => void;
  arrayExpandedState: Record<string, boolean>;
  onToggleArrayExpand: (path: string) => void;
}) {
  const hasChildren = node.children.length > 0;
  const isSelected = selectedPaths.includes(node.path);
  const expanded = hasChildren ? expandedState[node.path] : false;

  // Array condensation logic
  const arrayExpanded = arrayExpandedState[node.path] ?? false;
  const shouldCondenseArray = node.children.length > 3 && !arrayExpanded;

  const onChildTogglePath = useCallback(
    (path: string, selected: boolean) => {
      const convertedPath = getConvertedNegativePath(path, node);
      onTogglePath(convertedPath, selected);
    },
    [onTogglePath, node]
  );
  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-1">
        {hasChildren ? (
          <IconButton
            icon={ChevronRightIcon}
            label={expanded ? 'Collapse' : 'Expand'}
            variant="plain"
            color="secondary"
            size="sm"
            className="border-none"
            iconClassName={cn(
              'h-4 w-4 transition-transform',
              expanded && 'rotate-90'
            )}
            tooltipProps={{
              side: 'left',
            }}
            onClick={() => onToggleExpand(node.path)}
          />
        ) : (
          <div className="h-6 w-6 flex-none" />
        )}

        <label
          className="flex items-center gap-2 overflow-hidden text-sm"
          title={node.path}
        >
          <Checkbox
            checked={isSelected}
            onChange={(event) => onTogglePath(node.path, event.target.checked)}
            size="sm"
          />
          <span>
            {node.name}{' '}
            {expanded && node.children.length > 3 && (
              <Text variant="xs" color="secondary">
                ({node.children.length})
              </Text>
            )}
          </span>

          {(typeof node.value === 'string' ||
            typeof node.value === 'number') && (
            <TextOverflowTooltip className="flex-1 text-xs text-secondary">
              {node.value}
            </TextOverflowTooltip>
          )}
        </label>
      </div>

      {hasChildren && expanded && (
        <div className="pl-6">
          {shouldCondenseArray ? (
            <>
              {node.children.slice(0, 2).map((child) => (
                <PathTreeNode
                  key={child.path}
                  node={child}
                  depth={depth + 1}
                  selectedPaths={selectedPaths}
                  onTogglePath={onChildTogglePath}
                  expandedState={expandedState}
                  onToggleExpand={onToggleExpand}
                  arrayExpandedState={arrayExpandedState}
                  onToggleArrayExpand={onToggleArrayExpand}
                />
              ))}
              <Button
                variant="plain"
                color="secondary"
                size="sm"
                className="py-0.5"
                onClick={() => onToggleArrayExpand(node.path)}
              >
                <Text variant="xs" color="secondary" className="text-xxs">
                  ... expand all
                </Text>
              </Button>
              <PathTreeNode
                key={node.children[node.children.length - 1].path}
                node={node.children[node.children.length - 1]}
                depth={depth + 1}
                selectedPaths={selectedPaths}
                onTogglePath={onChildTogglePath}
                expandedState={expandedState}
                onToggleExpand={onToggleExpand}
                arrayExpandedState={arrayExpandedState}
                onToggleArrayExpand={onToggleArrayExpand}
              />
            </>
          ) : (
            <>
              {node.children.map((child) => (
                <PathTreeNode
                  key={child.path}
                  node={child}
                  depth={depth + 1}
                  selectedPaths={selectedPaths}
                  onTogglePath={onChildTogglePath}
                  expandedState={expandedState}
                  onToggleExpand={onToggleExpand}
                  arrayExpandedState={arrayExpandedState}
                  onToggleArrayExpand={onToggleArrayExpand}
                />
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

interface PathSectionProps {
  title: string;
  description?: string;
  availablePaths: string[];
  values: unknown;
  selectedPaths: string[];
  onChangeSelectedPaths: (paths: string[]) => void;
  defaultOpen?: boolean;
  placeholder?: string;
  containerClassName?: string;
}

function PathSection({
  title,
  description,
  availablePaths,
  values,
  selectedPaths,
  onChangeSelectedPaths,
}: PathSectionProps) {
  const [isOpen, setIsOpen] = useState(true);
  const [query, setQuery] = useState('');

  const filteredOptions = useFilteredPaths(availablePaths, query);
  const nodes = usePathTreeFromValues(values);

  const [expandedState, setExpandedState] = useState<Record<string, boolean>>(
    {}
  );
  const [arrayExpandedState, setArrayExpandedState] = useState<
    Record<string, boolean>
  >({});

  const toggleExpand = useCallback((path: string) => {
    setExpandedState((prev) => {
      // If collapsing, clean up any child array expanded states
      if (!prev[path]) {
        setArrayExpandedState((arrayPrev) => {
          const newArrayState = { ...arrayPrev };
          Object.keys(newArrayState).forEach((arrayPath) => {
            if (arrayPath.startsWith(`${path}`)) {
              delete newArrayState[arrayPath];
            }
          });
          return newArrayState;
        });
      }

      return { ...prev, [path]: !prev[path] };
    });
  }, []);

  const toggleArrayExpand = useCallback((path: string) => {
    setArrayExpandedState((prev) => ({ ...prev, [path]: !prev[path] }));
  }, []);

  const handlePathToggle = useCallback(
    (path: string, selected: boolean) => {
      if (selected && !selectedPaths.includes(path)) {
        onChangeSelectedPaths([...selectedPaths, path]);
      } else {
        onChangeSelectedPaths(selectedPaths.filter((item) => item !== path));
      }
    },
    [onChangeSelectedPaths, selectedPaths]
  );

  const handleTagChange = useCallback(
    (next: string[]) => {
      if (!arraysEqual(next, selectedPaths)) {
        onChangeSelectedPaths(next);
      }
    },
    [onChangeSelectedPaths, selectedPaths]
  );

  const handleSelectSuggestion = useCallback(
    (path: string) => {
      if (!selectedPaths.includes(path)) {
        onChangeSelectedPaths([...selectedPaths, path]);
      }
      setQuery('');
    },
    [onChangeSelectedPaths, selectedPaths]
  );
  // Interpolate negative indices in selected paths to positive equivalents
  const interpolatedSelectedPaths = useMemo(
    () => interpolateNegativeIndices(selectedPaths, values),
    [selectedPaths, values]
  );

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={setIsOpen}
      className={cn(
        'flex min-w-0 flex-col overflow-hidden',
        isOpen && 'basis-1/2'
      )}
    >
      <CollapsibleTrigger className="group flex items-center justify-between gap-2 bg-secondary px-4 py-3 text-left outline-none transition-colors hover:bg-secondary-hover">
        <Text variant="h4" color="secondary" weight="medium">
          {title}
        </Text>
        <ChevronDownIcon className="h-5 w-5 transition-transform group-data-[state=open]:rotate-180" />
      </CollapsibleTrigger>
      <CollapsibleContent className="transition-height flex flex-1 flex-col gap-4 overflow-hidden px-4 data-[state=closed]:h-0 data-[state=open]:h-[--radix-collapsible-content-height] data-[state=open]:pb-4 data-[state=open]:pt-2">
        <div className="flex flex-col gap-2">
          {description && (
            <Text variant="sm" color="secondary">
              {description}
            </Text>
          )}
          <TagInput
            value={selectedPaths}
            onChange={handleTagChange}
            query={query}
            onQueryChange={setQuery}
            filteredOptions={filteredOptions}
            placeholder="Select JSON paths"
            onSelectSuggestion={handleSelectSuggestion}
          />
        </div>
        <div className="flex-1 overflow-hidden rounded-md border border-secondary">
          <div className="h-full min-h-0 overflow-auto px-2 py-2">
            {nodes.length === 0 ? (
              <div className="py-6 text-center text-sm text-tertiary">
                No paths available
              </div>
            ) : (
              nodes.map((node) => (
                <PathTreeNode
                  node={node}
                  depth={0}
                  selectedPaths={interpolatedSelectedPaths}
                  onTogglePath={handlePathToggle}
                  expandedState={expandedState}
                  onToggleExpand={toggleExpand}
                  arrayExpandedState={arrayExpandedState}
                  onToggleArrayExpand={toggleArrayExpand}
                />
              ))
            )}
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

export function ConversationPathConfigurator({
  availableInputPaths,
  availableOutputPaths,
  inputValues,
  outputValues,
  selectedInputPaths,
  selectedOutputPaths,
  onChangeInputPaths,
  onChangeOutputPaths,
}: ConversationPathConfiguratorProps) {
  return (
    <div className="flex h-full flex-col overflow-hidden">
      <PathSection
        title="Input"
        description="Enter JSON paths to show in conversations. Preview will reflect your selections."
        availablePaths={availableInputPaths}
        values={inputValues}
        selectedPaths={selectedInputPaths}
        onChangeSelectedPaths={onChangeInputPaths}
      />

      <PathSection
        title="Output"
        description="Enter JSON paths to show in conversations. Preview will reflect your selections."
        availablePaths={availableOutputPaths}
        values={outputValues}
        selectedPaths={selectedOutputPaths}
        onChangeSelectedPaths={onChangeOutputPaths}
      />
    </div>
  );
}
